import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { environment } from '@env';
import { IndexComponent } from '@src/app/features/todo/pages/index/index.component';
import {
  AuthGuard,
  LoginComponent,
  LoginGuard,
  MsalOptionalGuard,
  NoPermissionComponent,
  PageNotFoundComponent
} from 'sigv-library';
import { ExtranetAuthGuard } from './core/guards/extranet-auth.guard';
import { ExtranetLoginGuard } from './core/guards/extranet-login.guard';
import { SystemInfoResolver } from './core/resolvers/system-info.resolver';
import { ForgetPasswordOtpComponent } from './extranet/components/forget-password-otp/forget-password-otp.component';
import { ForgotPasswordFormComponent } from './extranet/components/forgot-password-form/forgot-password-form.component';
import { LayoutComponent as ExtranetLayoutComponent } from './extranet/components/layout/layout.component';
import { LoginComponent as ExtranetLoginComponent } from './extranet/components/login/login.component';
import { LogoutComponent as ExtranetLogoutComponent } from './extranet/components/logout/logout.component';
import { RedirectionComponent as LoginRedirectionComponent } from './extranet/components/redirection/redirection.component';
import { UpdatePasswordFormComponent } from './extranet/components/update-password-form/update-password-form.component';
import { LayoutComponent } from './layout/components/layout/layout.component';

const getAuthenticationGard = (): any[] => {
  if (environment.isExtranet) {
    return [ExtranetAuthGuard];
  } else {
    return [MsalOptionalGuard, AuthGuard];
  }
};

const getLoginGuard = (): any[] => {
  if (environment.isExtranet) {
    return [ExtranetLoginGuard];
  } else {
    return [MsalOptionalGuard, LoginGuard];
  }
};

const routes: Routes = [
  {
    path: 'login',
    canActivate: [...getLoginGuard()],
    component: environment.isExtranet ? ExtranetLoginComponent : LoginComponent,
  },
  {
    path: 'logout',
    component: ExtranetLogoutComponent,
    resolve: { systemInfo: SystemInfoResolver },
  },
  {
    path: 'redirection',
    component: LoginRedirectionComponent,
  },
  {
    path: 'forgotPassword',
    component: ForgotPasswordFormComponent,
  },
  {
    path: 'forgotPasswordotp',
    component: ForgetPasswordOtpComponent,
  },
  {
    path: 'changePassword',
    canActivate: [...getAuthenticationGard()],
    component: UpdatePasswordFormComponent,
  },
  {
    path: 'no-permission',
    component: NoPermissionComponent,
  },
  {
    path: '',
    component: environment.isExtranet
      ? ExtranetLayoutComponent
      : LayoutComponent,
    resolve: environment.isExtranet ? { systemInfo: SystemInfoResolver } : {},
    canActivate: [...getAuthenticationGard()],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/web-submission/web-submission.module').then(
            (m) => m.WebSubmissionModule
          ),
      },
      {
        path: 'notification',
        loadChildren: () =>
          import('./features/notification/notification.module').then(
            (m) => m.NotificationModule
          ),
      },
      {
        path: 'todo-item/company/:companyId/process/:processDefKey/task/:taskDefKey',
        component: IndexComponent,
        data: { grantExternalAccess: true },
      },
      {
        path: ':companyId/todo-item/company/:companyId/process/:processDefKey/task/:taskDefKey',
        component: IndexComponent,
        data: { grantExternalAccess: true },
      },
      {
        path: 'setting',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/setting/setting.module').then(
                (m) => m.SettingModule
              ),
          },
        ],
      },
      {
        path: 'todo',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/todo/todo.module').then((m) => m.TodoModule),
          },
        ],
      },
      {
        path: 'reminder',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/reminder/reminder.module').then(
                (m) => m.ReminderModule
              ),
          },
        ],
      },
      {
        path: 'announcement',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/announcement/announcement.module').then(
                (m) => m.AnnouncementModule
              ),
          },
        ],
      },
      {
        path: 'insurance',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/insurance/insurance.module').then(
                (m) => m.InsuranceModule
              ),
          },
        ],
      },
      {
        path: 'search',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/search/search.module').then(
                (m) => m.SearchModule
              ),
          },
        ],
      },
      {
        path: 'funding-exp',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/funding-exp/fundingexp.module').then(
                (m) => m.FundingExpModule
              ),
          },
        ],
      },
      {
        path: 'news-detail',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/news/news.module').then((m) => m.NewsModule),
          },
        ],
      },
      {
        path: 'report',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/report/report.module').then(
                (m) => m.ReportModule
              ),
          },
        ],
      },
      {
        path: 'sales-quote',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/sales-quote/sales-quote.module').then(
                (m) => m.SalesQuoteModule
              ),
          },
        ],
      },
      // 萬用字元 path 前面匹配不到的路由都會進到這邊來
      // "一定"要設定在最後一個
      {
        path: '**',
        component: PageNotFoundComponent,
      },
      
    ],
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      relativeLinkResolution: 'legacy',
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
